import { useState } from "react"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { PersonalizedDashboard } from "@/components/apex/PersonalizedDashboard"
import { SmartNotifications } from "@/components/apex/SmartNotifications"
import { CountdownTimer } from "@/components/apex/CountdownTimer"
import { AnimatedCard } from "@/components/apex/AnimatedCard"
import { ProductCard } from "@/components/apex/ProductCard"
import { useApexState } from "@/hooks/useApexState"
import { 
  Bell,
  BellRing, 
  Crown,
  ShoppingBag,
  Zap,
  Calendar,
  ChevronRight,
  Star,
  TrendingUp
} from "lucide-react"

// Mock data
const userProfile = {
  name: "Alex Chen",
  tier: "creator" as const,
  points: 2840,
  nextTierPoints: 5000,
  nextTierName: "Architect"
}

const featuredProducts = [
  {
    id: "architect-hoodie",
    name: "Architect Hoodie",
    price: 220,
    image: "https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=300&h=400&fit=crop",
    isNew: false,
    isLimited: true,
    isApexLocked: true,
    discount: 0
  },
  {
    id: "kinetic-joggers",
    name: "Kinetic Joggers",
    price: 180,
    image: "https://images.unsplash.com/photo-1556905055-8f358a7a47b2?w=300&h=400&fit=crop",
    isNew: true,
    isLimited: false,
    isApexLocked: true,
    discount: 10
  },
  {
    id: "form-tank",
    name: "Form Training Tank",
    price: 85,
    image: "https://images.unsplash.com/photo-1583900985737-6d0495555783?w=300&h=400&fit=crop",
    isNew: false,
    isLimited: false,
    isApexLocked: false,
    discount: 0
  }
]

const liveDrops = [
  {
    id: "architect-collection",
    title: "Architect Collection",
    subtitle: "Limited Edition Drop",
    image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&h=300&fit=crop",
    launchDate: new Date(Date.now() + 2 * 60 * 60 * 1000), // 2 hours from now
    itemCount: 47,
    totalItems: 100,
    isLive: false,
    tierRequired: "creator"
  }
]

const recentActivity = [
  { action: "Style Challenge completed", points: 150, time: "2 hours ago" },
  { action: "Product purchased", points: 220, time: "1 day ago" },
  { action: "Community post liked", points: 5, time: "2 days ago" }
]

export function ApexHome() {
  const { state, actions } = useApexState()
  const [showNotifications, setShowNotifications] = useState(false)
  const [notificationCount] = useState(3) // Mock unread count

  // Use state.user if available, fallback to userProfile
  const user = state.user || userProfile
  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-lg">
        <div className="max-w-md mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Crown size={16} className="text-primary" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-foreground">Apex</h1>
                <p className="text-xs text-muted-foreground">Shape Your World</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowNotifications(true)}
                className="relative p-2"
              >
                {notificationCount > 0 ? (
                  <>
                    <BellRing size={20} className="text-foreground" />
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-black">{notificationCount}</span>
                    </div>
                  </>
                ) : (
                  <Bell size={20} className="text-foreground" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-md mx-auto px-4 py-6 space-y-6">
        {/* Personalized Dashboard */}
        <PersonalizedDashboard />

        {/* Live Drops Section */}
        {liveDrops.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Zap size={20} className="text-primary" />
                Live Drops
              </h3>
              <Button variant="ghost" size="sm" className="text-primary">
                View All
              </Button>
            </div>

            {liveDrops.map(drop => (
              <AnimatedCard key={drop.id} hoverScale>
                <Card className="bg-card-gradient border-0 overflow-hidden">
                  <div className="relative">
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={drop.image}
                        alt={drop.title}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className="bg-primary text-black">
                          {drop.isLive ? 'LIVE NOW' : 'COMING SOON'}
                        </Badge>
                        {drop.tierRequired && (
                          <Badge className="bg-tier-creator text-black">
                            <Crown size={10} className="mr-1" />
                            {drop.tierRequired}+ only
                          </Badge>
                        )}
                      </div>

                      <h3 className="text-xl font-bold text-white mb-1">
                        {drop.title}
                      </h3>
                      <p className="text-white/80 text-sm mb-3">
                        {drop.subtitle}
                      </p>

                      {!drop.isLive && (
                        <CountdownTimer 
                          targetDate={drop.launchDate}
                          size="sm"
                          className="mb-3"
                        />
                      )}

                      <div className="flex items-center justify-between">
                        <div className="text-white/80 text-sm">
                          {drop.itemCount} of {drop.totalItems} remaining
                        </div>
                        <Button 
                          size="sm"
                          className="bg-primary hover:bg-primary/90 text-black font-semibold"
                        >
                          {drop.isLive ? 'Shop Now' : 'Get Notified'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              </AnimatedCard>
            ))}
          </div>
        )}

        {/* Featured Products */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Star size={20} className="text-accent" />
              Featured
            </h3>
            <Button variant="ghost" size="sm" className="text-primary">
              <ShoppingBag size={16} className="mr-1" />
              Shop All
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {featuredProducts.slice(0, 4).map(product => (
              <AnimatedCard key={product.id} hoverScale>
                <ProductCard
                  {...product}
                  isWishlisted={state.wishlistedItems.includes(product.id)}
                  onWishlist={() => {
                    if (state.wishlistedItems.includes(product.id)) {
                      actions.removeFromWishlist(product.id)
                    } else {
                      actions.addToWishlist(product.id)
                    }
                  }}
                  onAddToCart={() => actions.addToCart(product)}
                  onClick={() => actions.showProductDetails(product)}
                />
              </AnimatedCard>
            ))}
          </div>
        </div>

        {/* Quick Stats Overview */}
        <div className="grid grid-cols-3 gap-4">
          <AnimatedCard hoverScale>
            <Card className="bg-card-gradient border-0">
              <CardContent className="p-4 text-center">
                <TrendingUp size={20} className="mx-auto text-accent mb-2" />
                <div className="text-lg font-bold text-foreground">
                  {state.user?.tier === 'creator' ? '68%' : state.user?.tier === 'architect' ? '100%' : '24%'}
                </div>
                <div className="text-xs text-muted-foreground">
                  Tier Progress
                </div>
              </CardContent>
            </Card>
          </AnimatedCard>

          <AnimatedCard hoverScale>
            <Card className="bg-card-gradient border-0">
              <CardContent className="p-4 text-center">
                <Zap size={20} className="mx-auto text-primary mb-2" />
                <div className="text-lg font-bold text-foreground">
                  {state.user?.points?.toLocaleString() || '0'}
                </div>
                <div className="text-xs text-muted-foreground">
                  Apex Points
                </div>
              </CardContent>
            </Card>
          </AnimatedCard>

          <AnimatedCard hoverScale>
            <Card className="bg-card-gradient border-0">
              <CardContent className="p-4 text-center">
                <Calendar size={20} className="mx-auto text-secondary mb-2" />
                <div className="text-lg font-bold text-foreground">4</div>
                <div className="text-xs text-muted-foreground">
                  Day Streak
                </div>
              </CardContent>
            </Card>
          </AnimatedCard>
        </div>

        {/* Community Spotlight */}
        <AnimatedCard>
          <Card className="bg-card-gradient border-0">
            <CardHeader>
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-foreground">Community Spotlight</h3>
                <Button variant="ghost" size="sm" className="text-primary">
                  <ChevronRight size={16} />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-muted/10 hover:bg-muted/20 transition-colors cursor-pointer">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-accent"></div>
                    <div className="flex-1">
                      <h4 className="font-medium text-foreground text-sm">Motion & Form Challenge</h4>
                      <p className="text-xs text-muted-foreground">127 participants • 8 hours left</p>
                    </div>
                    <Badge className="bg-accent text-accent-foreground text-xs">
                      +500 pts
                    </Badge>
                  </div>
                </div>

                <div className="p-3 rounded-lg bg-muted/10 hover:bg-muted/20 transition-colors cursor-pointer">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500"></div>
                    <div className="flex-1">
                      <h4 className="font-medium text-foreground text-sm">NYC Run Club</h4>
                      <p className="text-xs text-muted-foreground">Tomorrow 9:00 AM • 24 attending</p>
                    </div>
                    <Button size="sm" variant="outline" className="text-xs">
                      RSVP
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </AnimatedCard>
      </div>

      {/* Smart Notifications Modal */}
      <SmartNotifications 
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
      />
    </div>
  )
}